import { IonButton, IonInput, IonModal } from "@ionic/react";
import React from "react";

import { Context } from "../../../../store/teacher";
import {
  IClass,
  ITeacherGearbox,
  TNameStatus,
  TeacherGearboxDirectory,
  GearboxTemplate,
} from "../../../../state";
import ToggleSwitch from "../../../../atoms/ToggleSwitch";
import Toast from "../../../../molecules/Toast";
import ClassSelectForm from "../../../../molecules/ClassSelectForm";
import BoxTemplateSelector from "../../../../molecules/BoxTemplateSelector";
import BoxDirectorySelectForm from "../../../../molecules//BoxDirectorySelectForm";

import styles from "./BoxForm.module.scss";

const switchNameStatusKeyValue = (status: TNameStatus | boolean) => {
  const nameStatusKeyValue: { [key: string]: string | boolean } = {
    publish: true,
    unpublish: false,
    true: "publish",
    false: "unpublish",
  };
  return nameStatusKeyValue[`${status}`];
};

export interface BoxFormProps {
  calling: boolean;
  isOpen: boolean;
  classes: IClass[];
  teacherGearboxDirectories: TeacherGearboxDirectory[];
  defaultTitle: string;
  defaultTemplateId: number | null;
  id: number;
  classIDs: number[];
  teacherGearboxDirectoryIDs: number[];
  nameStatus: TNameStatus;
  templates: GearboxTemplate[];
  is_archived?: boolean;
  is_shared: boolean;
  subject_id: number | null;
  gearboxes: ITeacherGearbox[];
  close: () => void;
  ok: (box: {
    id: number;
    title: string;
    class_ids: number[];
    teacher_gearbox_directory_ids: number[];
    name_status: TNameStatus;
    is_archived: boolean;
    is_hidden: boolean;
    is_shared: boolean;
    gearbox_template_id: number | null;
  }) => void;
}

const BoxForm: React.FC<BoxFormProps> = ({
  calling,
  isOpen,
  classes,
  teacherGearboxDirectories,
  defaultTitle,
  defaultTemplateId,
  id,
  classIDs,
  teacherGearboxDirectoryIDs,
  nameStatus,
  gearboxes,
  templates,
  is_archived = false,
  is_shared,
  subject_id,
  close,
  ok,
}) => {
  const { contextState } = React.useContext(Context);

  const [state, setState] = React.useState<{
    showShareForm: { class: boolean; directory: boolean };
    showToast: boolean;
    message: string;
    title: string;
    selected_class_ids: number[];
    selectedTeacherGearboxDirectoryIDs: number[];
    nameStatus: TNameStatus;
    is_archived: boolean;
    is_hidden: boolean;
    is_shared: boolean;
    selected_template_id: number | null;
  }>({
    showShareForm: { class: false, directory: false },
    showToast: false,
    message: "",
    title: "",
    selected_class_ids: [],
    selectedTeacherGearboxDirectoryIDs: [],
    nameStatus: "publish",
    is_archived: false,
    is_hidden: false,
    is_shared: false,
    selected_template_id: defaultTemplateId,
  });

  const selected_template = templates?.find(
    (template: GearboxTemplate) => template.id === state.selected_template_id,
  );

  const update = React.useCallback(
    (args: { [key: string]: any }) => {
      const selected_class_ids =
        args.selected_class_ids ?? state.selected_class_ids;

      if (selected_class_ids.length > 1) {
        args.is_shared = true;
      }
      if (
        args.selected_class_ids?.length === 1 &&
        state.selected_class_ids.length === 2
      ) {
        args.is_shared = false;
      }
      setState(prevState => ({ ...prevState, ...args }));
    },
    [state.selected_class_ids],
  );

  React.useEffect(() => {
    if (id) {
      update({
        title: defaultTitle,
        selected_class_ids: classIDs,
        selectedTeacherGearboxDirectoryIDs: teacherGearboxDirectoryIDs,
        nameStatus,
        is_archived,
        is_shared,
        selected_template_id: defaultTemplateId,
      });
    } else {
      const currentYearDirectoryId =
        teacherGearboxDirectories.find(
          directory =>
            directory.is_system &&
            directory.name === `${contextState.current_year}`,
        )?.id ?? null;

      update({
        title: "",
        selected_class_ids: [],
        selectedTeacherGearboxDirectoryIDs: currentYearDirectoryId
          ? [currentYearDirectoryId]
          : [],
        nameStatus: "publish",
        is_archived: false,
        is_shared: false,
        selected_template_id: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  React.useEffect(() => {
    if (!id) return;

    // 現在のBOXに対応するディレクトリIDを取得
    const gearBox = gearboxes.find(gearbox => gearbox.id === id);
    const defaultDirectoryIDs = gearBox
      ? gearBox.teacher_gearbox_directory_ids
      : [];

    // ディレクトリIDを更新
    update({ selectedTeacherGearboxDirectoryIDs: defaultDirectoryIDs });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = React.useCallback(() => {
    if (subject_id && !state.title) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "BOX名を入力してください。",
      }));
      return;
    }
    if (
      !subject_id &&
      (!state.title || state.selected_class_ids.length === 0)
    ) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "BOX名とクラスを入力してください。",
      }));
      return;
    }
    ok({
      id: id || 0,
      title: state.title,
      class_ids: state.selected_class_ids,
      teacher_gearbox_directory_ids: state.selectedTeacherGearboxDirectoryIDs,
      name_status: state.nameStatus,
      is_archived: state.is_archived,
      is_hidden: state.is_hidden,
      is_shared: state.is_shared,
      gearbox_template_id: state.selected_template_id,
    });
  }, [
    id,
    subject_id,
    state.title,
    state.selected_class_ids,
    state.selectedTeacherGearboxDirectoryIDs,
    state.nameStatus,
    state.is_archived,
    state.is_hidden,
    state.is_shared,
    state.selected_template_id,
    ok,
  ]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={close} className={styles.wrapper}>
      <Toast
        type="danger"
        showToast={state.showToast}
        onClose={() => setState(s => ({ ...s, showToast: false, message: "" }))}
        message={state.message}
      />
      <div className={styles.form}>
        <div className={styles.label}>{id ? "BOX名を編集" : "BOXをつくる"}</div>
        <div className={styles.inputArea}>
          <IonInput
            className={styles.input}
            name="gearboxTitle"
            value={state.title}
            placeholder="BOX名を記入"
            required
            onIonChange={e => update({ title: e.detail.value })}
          />
        </div>
        <div className={styles.template}>
          <BoxTemplateSelector
            defaultTemplateId={state.selected_template_id}
            templates={templates}
            handleChangeTemplate={(id: number | null) =>
              update({ selected_template_id: id })
            }
          />
          {selected_template && (
            <div className={styles.explanation}>
              <p className={styles.read}>
                生徒側の画面には、入力のサポートとして以下が表示されるようになります。
              </p>
              <p className={styles.text}>{selected_template.content}</p>
            </div>
          )}
        </div>
        {!subject_id && (
          <ClassSelectForm
            isOpen={state.showShareForm.class}
            label="クラスを選択"
            classes={classes}
            multiple
            selected_class_ids={state.selected_class_ids}
            className={styles.classSelectForm}
            whiteMode
            onClick={() => {
              setState({
                ...state,
                showShareForm: {
                  ...state.showShareForm,
                  class: !state.showShareForm.class,
                },
              });
            }}
            update={update}
          />
        )}
        <BoxDirectorySelectForm
          isOpen={state.showShareForm.directory}
          label="フォルダを選択"
          teacherGearboxDirectories={teacherGearboxDirectories}
          selectedTeacherGearboxDirectoryIDs={
            state.selectedTeacherGearboxDirectoryIDs
          }
          className={styles.boxDirectorySelectForm}
          onClick={() => {
            setState({
              ...state,
              showShareForm: {
                ...state.showShareForm,
                directory: !state.showShareForm.directory,
              },
            });
          }}
          update={update}
        />
        <div className={styles.switchWrapper}>
          <ToggleSwitch
            id="name_status"
            label="生徒名を公開"
            defaultCheck={!!switchNameStatusKeyValue(state.nameStatus)}
            onSwitch={check =>
              update({ nameStatus: switchNameStatusKeyValue(check) })
            }
          />
        </div>
        <div className={styles.switchWrapper}>
          <ToggleSwitch
            id="is_archived"
            label="提出を停止"
            defaultCheck={state.is_archived}
            onSwitch={check => update({ is_archived: check })}
          />
        </div>
        {!subject_id && (
          <div className={styles.switchWrapper}>
            <ToggleSwitch
              id="is_shared"
              label="選択クラスの他の担当先生にも表示する"
              defaultCheck={state.is_shared}
              onSwitch={check => update({ is_shared: check })}
              disabled={state.selected_class_ids.length > 1}
            />
          </div>
        )}
        <div className={styles.buttonArea}>
          <IonButton
            className={styles.cancelButton}
            strong
            fill="outline"
            onClick={close}
          >
            キャンセル
          </IonButton>
          <IonButton
            className={styles.postButton}
            disabled={calling}
            strong
            onClick={onSubmit}
          >
            保存
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default BoxForm;
